import Btn from '@app/lui2/Btn';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {createOutlookEmailSync, fetchOutlookCurrentUser} from '../../requests';
import {requestSetOutlookEmailSyncHeader} from '../../sendDialogParentMessages';
import {usePodRequest} from '../../usePodRequest';
import ModalContainer from '../ModalContainer';
import {Subtitle, Title} from '../styles';
import EntityLinks from './EntityLinks';
import {fetchPersonLinksForEmails} from './personLinkHelpers';
import {ErrorMessage, Header, Loading} from './styles';
import {useComposeMessageAttachments} from './useComposeMessageAttachments';
import {useComposeMessageMetadata} from './useComposeMessageMetadata';
import {useEmailSyncLinkState} from './useEmailSyncLinkState';

interface Props {
  onCancel: () => void;
  onSubmit: () => void | Promise<void>;
}

const isAbortError = (error: unknown): boolean => {
  return error instanceof DOMException && error.name === 'AbortError';
};

const EmailSync = ({onCancel, onSubmit}: Props) => {
  const {request, postRequest} = usePodRequest();
  const [headerId] = useState(() => crypto.randomUUID());
  const linkState = useEmailSyncLinkState();
  const {personLinks, setPersonLinks, companyLinks, jobLinks, dealLinks} =
    linkState;
  const attachments = useComposeMessageAttachments();
  const composeMetadata = useComposeMessageMetadata();
  const [agencyId, setAgencyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {fromAddress, subject, recipients} = composeMetadata;
  const recipientEmailsKey = recipients
    .map((recipient) => recipient.email)
    .join(',');
  const recipientEmails = useMemo(
    () => (recipientEmailsKey ? recipientEmailsKey.split(',') : []),
    [recipientEmailsKey]
  );

  useEffect(() => {
    if (!composeMetadata.ready) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const isActive = () => !cancelled && !controller.signal.aborted;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const currentUser = await fetchOutlookCurrentUser(request, {
          signal: controller.signal,
        });
        const loadedAgencyId = currentUser.current_user.first_agency?.id;
        if (isActive()) {
          setAgencyId(loadedAgencyId ?? null);
        }
        if (!loadedAgencyId) {
          if (isActive()) {
            setLoadError('Agency is not available for this account.');
            setPersonLinks([]);
          }
          return;
        }

        const links = await fetchPersonLinksForEmails(
          request,
          loadedAgencyId,
          recipientEmails,
          {signal: controller.signal}
        );
        if (isActive()) {
          setPersonLinks(links);
        }
      } catch (error) {
        if (isActive() && !isAbortError(error)) {
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Failed to load email sync options.'
          );
          setPersonLinks([]);
          setAgencyId(null);
        }
      } finally {
        if (isActive()) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [composeMetadata.ready, recipientEmails, request, setPersonLinks]);

  const completeEmailSync = useCallback(
    async (sync: boolean) => {
      if (agencyId == null) {
        setSubmitError('Agency is not available for this account.');
        return;
      }

      setSubmitting(true);
      setSubmitError(null);

      try {
        await requestSetOutlookEmailSyncHeader(headerId);
        const result = await createOutlookEmailSync(postRequest, {
          agencyId,
          headerId,
          sync,
          subject,
          fromAddress,
          personLinks: sync ? personLinks : [],
          companyLinks: sync ? companyLinks : [],
          jobLinks: sync ? jobLinks : [],
          dealLinks: sync ? dealLinks : [],
          recipients,
          attachments,
        });
        if (!result.ok) {
          setSubmitError(
            result.errors?.join(', ') || 'Failed to save email sync'
          );
          return;
        }
        await onSubmit();
      } catch (error) {
        setSubmitError(
          error instanceof Error ? error.message : 'Failed to save email sync'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      agencyId,
      attachments,
      companyLinks,
      dealLinks,
      fromAddress,
      headerId,
      jobLinks,
      onSubmit,
      personLinks,
      postRequest,
      recipients,
      subject,
    ]
  );

  const handleDoNotSync = () => {
    void completeEmailSync(false);
  };

  const handleSync = () => {
    void completeEmailSync(true);
  };

  if (loading || !composeMetadata.ready) {
    return <Loading>Loading…</Loading>;
  }

  return (
    <ModalContainer
      footerChildren={
        <>
          <Btn onClick={onCancel} disabled={submitting}>
            Cancel Send
          </Btn>
          <Btn onClick={handleDoNotSync} disabled={submitting}>
            Only Send
          </Btn>
          <Btn
            onClick={handleSync}
            variant="filled"
            color="primary"
            disabled={submitting}
          >
            Save & Send
          </Btn>
        </>
      }
    >
      <Header>
        <Title>Sync Email to Loxo</Title>
        <Subtitle>
          If you want to save this email thread in Loxo, choose the items it
          relates to.
        </Subtitle>
      </Header>

      {submitError && <ErrorMessage>{submitError}</ErrorMessage>}
      {loadError && <ErrorMessage>{loadError}</ErrorMessage>}

      {agencyId != null && (
        <EntityLinks
          agencyId={agencyId}
          contextEmails={recipientEmails}
          attachments={attachments}
          {...linkState}
        />
      )}
    </ModalContainer>
  );
};

export default EmailSync;
